import React from 'react';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { BTN } from '../styles/tokens';

type Severity = 'info' | 'warning' | 'error';

interface AlertDialogProps {
  title: string;
  message: string;
  /** 預設「我知道了」 */
  confirmLabel?: string;
  onClose: () => void;
  severity?: Severity;
}

const ICON_MAP: Record<Severity, { Icon: React.FC<{ size?: number; className?: string }>; iconColor: string; bgColor: string; btnColor: string }> = {
  info: {
    Icon: Info,
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    btnColor: BTN.primary,
  },
  warning: {
    Icon: AlertTriangle,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    btnColor: BTN.warning,
  },
  error: {
    Icon: AlertCircle,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    btnColor: BTN.danger,
  },
};

const AlertDialog: React.FC<AlertDialogProps> = ({
  title,
  message,
  confirmLabel = '我知道了',
  onClose,
  severity = 'warning',
}) => {
  const { Icon, iconColor, bgColor, btnColor } = ICON_MAP[severity];
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-4 md:p-6 w-full max-w-sm mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${bgColor} flex items-center justify-center`}>
            <Icon size={20} className={iconColor} />
          </div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 leading-relaxed whitespace-pre-line">{message}</p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm ${btnColor}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDialog;
